import type { RouteObject } from 'react-router-dom'
import { Root, HomeRedirect } from './Root'
import { isLandingOnlyMode } from './lib/runtime'
import ErrorPage from './pages/ErrorPage'
import WorkspaceStatus from './components/WorkspaceStatus'
import RouteErrorPage from './components/RouteErrorPage'
import ProtectedRoute from './components/ProtectedRoute'
import { blogIndexLoader, blogPostLoader } from './pages/blog/blogLoaders'

type RenderMode = 'prerender' | 'ssr' | 'csr'
const tag = (m: RenderMode) => ({ renderMode: m })

const landingOnly = isLandingOnlyMode()

// Lazy page loaders — each route gets its own chunk so a visitor only
// downloads the JS for the page they land on. createStaticHandler awaits
// these before render, so prerender/SSR still emit full HTML.
const lazyDefault = (loader: () => Promise<{ default: React.ComponentType }>) => () =>
  loader().then((m) => ({ Component: m.default }))

// Picks SubdomainWelcome (workspace lobby) vs WelcomeLanding by the gateway
// `foxia_ws_context` cookie; falls back to WelcomeLanding during SSR/prerender.
const welcomeLanding = lazyDefault(() => import('./pages/welcome/WelcomeEntry'))

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Root />,
    errorElement: <RouteErrorPage />,
    children: [
      // ── PRERENDER (build-time static) ─────────────────────────────────────
      ...(landingOnly ? [{ index: true, handle: tag('prerender'), lazy: welcomeLanding }] : []),
      { path: 'welcome', handle: tag('prerender'), lazy: welcomeLanding },
      { path: 'welcome/crm', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/foxia/FoxiaCrm')) },
      { path: 'welcome/call', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/foxia/FoxiaCall')) },
      { path: 'welcome/chat', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/foxia/FoxiaChat')) },
      { path: 'welcome/dms', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/foxia/FoxiaDms')) },
      { path: 'welcome/dms/download', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/foxia/FoxiaDmsDownload')) },
      { path: 'welcome/hrm', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/foxia/FoxiaHrm')) },
      { path: 'welcome/work', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/foxia/FoxiaWork')) },
      { path: 'welcome/ads', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/welcome/WelcomeAds')) },
      { path: 'welcome/ai-agent', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/welcome/WelcomeAIAgent')) },
      { path: 'welcome/quote', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/Quote/QuotePage')) },
      { path: 'resale-strategy', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/ResaleStrategy')) },
      { path: 'privacy-policy', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/legal/LegalPage').then((m) => ({ default: m.PrivacyPolicyPage }))) },
      { path: 'terms-of-service', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/legal/LegalPage').then((m) => ({ default: m.TermsOfServicePage }))) },
      { path: 'tai-lieu', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/documents/DocumentsIndex')) },
      { path: 'tai-lieu/:slug', handle: tag('prerender'), lazy: lazyDefault(() => import('./pages/documents/DocumentDetail')) },
      // Legacy blog slugs: prerendered as bespoke TSX until API migration (Phase 5)
      {
        path: 'welcome/blog/crm-trong-thoi-dai-ai-khi-phan-mem-khong-con-chi-quan-ly-khach-hang-ma-tro-thanh-bo-nao-van-hanh-doanh-nghiep',
        handle: tag('prerender'),
        lazy: lazyDefault(() => import('./pages/foxia/blog/BlogCrm')),
      },
      {
        path: 'welcome/blog/ltv-cac-chi-so-am-tham-quyet-dinh-doanh-nghiep-cua-ban-dang-tang-truong-that-hay-chi-dang-dot-tien-de-lon-len',
        handle: tag('prerender'),
        lazy: lazyDefault(() => import('./pages/foxia/blog/LtvCacGrowthBlog')),
      },
      {
        path: 'welcome/blog/call-center-dang-buoc-sang-mot-ky-nguyen-moi-khi-ai-khong-chi-nghe-cuoc-goi-ma-con-hieu-khach-hang',
        handle: tag('prerender'),
        lazy: lazyDefault(() => import('./pages/foxia/blog/BlogCall')),
      },

      // ── SSR (runtime, Node service) ───────────────────────────────────────
      {
        path: 'welcome/blog',
        handle: tag('ssr'),
        loader: blogIndexLoader,
        lazy: lazyDefault(() => import('./pages/foxia/blog/BlogWelcome')),
      },
      {
        path: 'welcome/blog/:slug',
        handle: tag('ssr'),
        loader: blogPostLoader,
        // A missing post throws a 404 Response in the loader; render the 404
        // page here so crawlers get a real 404 status (via context.statusCode
        // in entry-server) instead of the SPA shell at a junk URL.
        errorElement: <ErrorPage code={404} />,
        lazy: lazyDefault(() => import('./pages/blog/BlogPost')),
      },

      // ── CSR (client-only, lazy) ───────────────────────────────────────────
      {
        path: 'auth/callback',
        handle: tag('csr'),
        lazy: () => import('./pages/AuthCallback').then((m) => ({ Component: m.default })),
      },
      {
        path: '404',
        handle: tag('csr'),
        element: <ErrorPage code={404} />,
      },
      {
        path: '500',
        handle: tag('csr'),
        element: <ErrorPage code={500} />,
      },
      {
        path: '502',
        handle: tag('csr'),
        element: <WorkspaceStatus />,
      },
      {
        path: 'error',
        handle: tag('csr'),
        element: <ErrorPage />,
      },

      // Protected routes
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: 'onboarding',
            handle: tag('csr'),
            lazy: () => import('./pages/OnboardingPage').then((m) => ({ Component: m.default })),
          },
          {
            path: 'upgrade',
            handle: tag('csr'),
            lazy: () => import('./pages/UpgradePage').then((m) => ({ Component: m.default })),
          },
          {
            path: 'setup',
            handle: tag('csr'),
            lazy: () => import('./pages/SetupProgressPage').then((m) => ({ Component: m.default })),
          },
          {
            path: 'notifications/:eventID',
            handle: tag('csr'),
            lazy: () => import('./pages/NotificationRedirect').then((m) => ({ Component: m.default })),
          },
          {
            path: 'settings',
            handle: tag('csr'),
            lazy: () => import('./pages/settings/SettingsLayout').then((m) => ({ Component: m.default })),
            children: [
              {
                index: true,
                lazy: () => import('react-router-dom').then(({ Navigate }) => ({
                  Component: () => <Navigate to="/settings/domains" replace />,
                })),
              },
              {
                path: 'domains',
                lazy: () => import('./pages/settings/DomainsPage').then((m) => ({ Component: m.default })),
              },
              {
                path: 'branding',
                lazy: () => import('./pages/settings/BrandingPage').then((m) => ({ Component: m.default })),
              },
            ],
          },
          {
            path: 'web/*',
            handle: tag('csr'),
            element: <WorkspaceStatus />,
          },
          {
            path: 'odoo/*',
            handle: tag('csr'),
            element: <WorkspaceStatus />,
          },
          {
            path: 'foxia/*',
            handle: tag('csr'),
            element: <WorkspaceStatus />,
          },
          {
            index: !landingOnly,
            handle: tag('csr'),
            element: <HomeRedirect />,
          },
          {
            path: '*',
            handle: tag('csr'),
            element: <ErrorPage code={404} />,
          },
        ],
      },
    ],
  },
]
